import { ChevronsUpDown, ClipboardX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuestionScoreRow } from './types';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';

interface TranscriptMessage {
  role: string;
  content: string;
  copyPasteDetected?: boolean;
  createdAt: string;
}

interface TranscriptSegment {
  questionId: string;
  startMessageIndex: number;
  endMessageIndex: number;
}

interface TranscriptViewProps {
  messages: TranscriptMessage[];
  segments?: TranscriptSegment[];
  questionScores?: QuestionScoreRow[];
}

function MessageBubble({ msg }: { msg: TranscriptMessage }): React.ReactNode {
  const { t } = useTranslation();
  const isFlagged = msg.role !== 'assistant' && msg.copyPasteDetected;
  return (
    <div className={`flex flex-col ${msg.role === 'assistant' ? 'items-start' : 'items-end'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          msg.role === 'assistant'
            ? 'bg-secondary text-secondary-foreground'
            : isFlagged
              ? 'bg-primary text-primary-foreground ring-2 ring-amber-400 ring-offset-1'
              : 'bg-primary text-primary-foreground'
        }`}
      >
        {msg.content}
      </div>
      {isFlagged && (
        <div className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
          <ClipboardX className="size-3" />
          <span>{t('scoring.copyPasteDetected')}</span>
        </div>
      )}
    </div>
  );
}

export function TranscriptView({
  messages,
  segments,
  questionScores
}: TranscriptViewProps): React.ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const questionTextMap = useMemo(() => {
    const map = new Map<string, string>();
    if (questionScores) {
      for (const qs of questionScores) {
        map.set(qs.questionId, qs.questionText);
      }
    }
    return map;
  }, [questionScores]);

  const hasSegments = segments && segments.length > 0;

  if (messages.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.transcript')}</h2>
        <CollapsibleTrigger className="inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
          <ChevronsUpDown className="size-4" />
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          {hasSegments
            ? renderSegmentedTranscript(messages, segments, questionTextMap)
            : messages.map((msg) => <MessageBubble key={msg.createdAt} msg={msg} />)}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function renderSegmentedTranscript(
  messages: TranscriptMessage[],
  segments: TranscriptSegment[],
  questionTextMap: Map<string, string>
): React.ReactNode[] {
  const sortedSegments = [...segments].sort((a, b) => a.startMessageIndex - b.startMessageIndex);
  const elements: React.ReactNode[] = [];
  let lastEnd = -1;

  for (const seg of sortedSegments) {
    // Render any gap messages before this segment
    if (seg.startMessageIndex > lastEnd + 1) {
      for (let i = lastEnd + 1; i < seg.startMessageIndex; i++) {
        const gapMsg = messages[i];
        if (gapMsg) {
          elements.push(<MessageBubble key={`gap-${String(i)}`} msg={gapMsg} />);
        }
      }
    }

    const questionText = questionTextMap.get(seg.questionId);
    const endIdx = Math.min(seg.endMessageIndex, messages.length - 1);
    elements.push(
      <div
        key={`seg-${seg.questionId}`}
        id={`transcript-q-${seg.questionId}`}
        className="flex flex-col gap-3 pt-2"
      >
        {questionText && (
          <div className="mb-2 border-b border-border pb-1">
            <p className="text-xs font-medium text-muted-foreground">{questionText}</p>
          </div>
        )}
        {Array.from({ length: endIdx - seg.startMessageIndex + 1 }, (_, j) => {
          const idx = seg.startMessageIndex + j;
          const segMsg = messages[idx];
          return segMsg ? (
            <MessageBubble key={`seg-${seg.questionId}-${String(idx)}`} msg={segMsg} />
          ) : null;
        })}
      </div>
    );

    lastEnd = endIdx;
  }

  // Render any remaining messages after last segment
  for (let i = lastEnd + 1; i < messages.length; i++) {
    const tailMsg = messages[i];
    if (tailMsg) {
      elements.push(<MessageBubble key={`tail-${String(i)}`} msg={tailMsg} />);
    }
  }

  return elements;
}
